import { computed, signal } from '@angular/core';
import { DEFAULT_ROWS_PER_PAGE } from 'src/app/common/constants-ejb';
import { ContactoPersona } from 'src/app/domain/model/persona/contacto-persona';
import { CursorPageResponse } from 'src/app/domain/model/cumplimiento/cumplimiento';
import { DireccionCursor } from 'src/app/domain/model/common/page-response.dto';

/** Allowlist del backend (seguridad-girop §2.3): NEXT, PREVIOUS o LAST. */
export type DireccionPagina = DireccionCursor;

/** Consulta UNA página al backend (keyset/cursor). */
export type ConsultarPaginaContactos = (
  cursor: string | null,
  size: number,
  direction: DireccionPagina
) => Promise<CursorPageResponse<ContactoPersona>>;

/**
 * Estado de paginación REAL en servidor que consume el diálogo de contactos
 * (`contactos` contiene solo la página actual).
 */
export interface PaginacionServidorContactos {
  pagina: number;
  tamano: number;
  total: number;
  totalPaginas: number;
  hayAnterior: boolean;
  haySiguiente: boolean;
  inicio: number;
  fin: number;
}

export type AccionPaginacionContactos =
  | { tipo: 'first' | 'previous' | 'next' | 'last' }
  | { tipo: 'size'; size: number };

interface UltimaConsulta {
  cursor: string | null;
  direction: DireccionPagina;
}

/** Página a mostrar una vez llega la respuesta (LAST la calcula con el total). */
type PaginaDestino =
  number | ((resp: CursorPageResponse<ContactoPersona>) => number);

/**
 * Paginación REAL en servidor para listados de contactos (skill frontend-girop §17 y
 * acceso-datos-oracle §7): en memoria solo existe la página actual; cada cambio de
 * página es una consulta al backend con el cursor devuelto por la página anterior.
 *
 * Legacy: SuraTable con setExternalPagination(true) y pageSize=10 (start/length).
 */
export class ContactosCursorPager {
  readonly items = signal<ContactoPersona[]>([]);
  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly totalRecords = signal(0);
  readonly loading = signal(false);
  private readonly hayMasPaginas = signal(false);
  private readonly hayPaginasAnteriores = signal(false);

  private ultimaRespuesta: CursorPageResponse<ContactoPersona> | null = null;
  private ultimaConsulta: UltimaConsulta = { cursor: null, direction: 'NEXT' };

  readonly totalPages = computed(() => {
    const total = this.totalRecords();
    const size = this.pageSize();
    if (total > 0) return Math.ceil(total / size);
    // Total desconocido: al menos la página actual (+1 si el backend indica que hay más)
    return this.items().length > 0 ? this.currentPage() + (this.hayMasPaginas() ? 1 : 0) : 0;
  });
  // frontend-girop §17.7: Anterior/Siguiente dependen de la respuesta, no de currentPage
  readonly hasPrevious = computed(() => this.hayPaginasAnteriores());
  readonly hasNext = computed(() => this.hayMasPaginas());
  readonly registroInicio = computed(() =>
    this.totalRecords() === 0 && this.items().length === 0
      ? 0
      : (this.currentPage() - 1) * this.pageSize() + 1
  );
  readonly registroFin = computed(() =>
    this.items().length === 0 ? 0 : this.registroInicio() + this.items().length - 1
  );

  /** Estado listo para el diálogo de contactos. */
  readonly paginacion = computed<PaginacionServidorContactos>(() => ({
    pagina: this.currentPage(),
    tamano: this.pageSize(),
    total: this.totalRecords(),
    totalPaginas: this.totalPages(),
    hayAnterior: this.hasPrevious(),
    haySiguiente: this.hasNext(),
    inicio: this.registroInicio(),
    fin: this.registroFin()
  }));

  constructor(private readonly consultar: ConsultarPaginaContactos) {}

  /** Vuelve a la primera página (p. ej. al abrir el diálogo o al cambiar de filtro). */
  cargarPrimera(): Promise<void> {
    return this.ejecutar(null, 'NEXT', 1);
  }

  siguiente(): Promise<void> {
    // Un doble clic no debe avanzar dos páginas con el mismo cursor (§17.7)
    if (this.loading()) return Promise.resolve();
    const cursor = this.ultimaRespuesta?.nextCursor ?? null;
    if (!this.hasNext() || !cursor) return Promise.resolve();
    return this.ejecutar(cursor, 'NEXT', this.currentPage() + 1);
  }

  anterior(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return Promise.resolve();
    const cursor = this.ultimaRespuesta?.previousCursor ?? null;
    // Sin cursor previo (no debería ocurrir si el backend informa hasPrevious): volver al inicio
    if (!cursor) return this.cargarPrimera();
    return this.ejecutar(cursor, 'PREVIOUS', this.currentPage() - 1);
  }

  /**
   * Última página: direction=LAST con cursor vacío (§17.7). El backend devuelve el resto
   * total % size, así que la página es ceil(totalElements / size) de esa misma respuesta.
   */
  ultima(): Promise<void> {
    if (this.loading()) return Promise.resolve();
    return this.ejecutar(null, 'LAST', (resp) => this.paginaDeUltima(resp));
  }

  /** Número de la última página según el total de la respuesta: ceil(totalElements / size). */
  private paginaDeUltima(resp: CursorPageResponse<ContactoPersona>): number {
    return Math.max(1, Math.ceil((resp.totalElements ?? 0) / this.pageSize()));
  }

  cambiarTamano(size: number): Promise<void> {
    this.pageSize.set(size);
    return this.cargarPrimera();
  }

  /** Ejecuta la acción de paginación pedida por el diálogo. */
  paginar(accion: AccionPaginacionContactos): Promise<void> {
    switch (accion.tipo) {
      case 'first':
        return this.cargarPrimera();
      case 'previous':
        return this.anterior();
      case 'next':
        return this.siguiente();
      case 'last':
        return this.ultima();
      case 'size':
        return this.cambiarTamano(accion.size);
    }
  }

  /**
   * Repite la consulta de la página actual (tras crear, editar o eliminar un contacto).
   * Si la página quedó vacía y no es la primera, retrocede una.
   */
  async recargar(): Promise<void> {
    const { cursor, direction } = this.ultimaConsulta;
    // LAST devuelve la última página vigente: su número sale del total de la nueva respuesta
    const pagina: PaginaDestino =
      direction === 'LAST'
        ? (resp) => this.paginaDeUltima(resp)
        : this.currentPage();
    await this.ejecutar(cursor, direction, pagina);
    if (this.items().length === 0 && this.currentPage() > 1) {
      await this.cargarPrimera();
    }
  }

  vaciar(): void {
    this.items.set([]);
    this.totalRecords.set(0);
    this.currentPage.set(1);
    this.hayMasPaginas.set(false);
    this.hayPaginasAnteriores.set(false);
    this.ultimaRespuesta = null;
    this.ultimaConsulta = { cursor: null, direction: 'NEXT' };
  }

  private async ejecutar(
    cursor: string | null,
    direction: DireccionPagina,
    pagina: PaginaDestino
  ): Promise<void> {
    this.loading.set(true);
    try {
      const resp = await this.consultar(cursor, this.pageSize(), direction);
      this.ultimaRespuesta = resp;
      this.ultimaConsulta = { cursor, direction };
      this.items.set(resp.content ?? []);
      this.totalRecords.set(resp.totalElements && resp.totalElements > 0 ? resp.totalElements : 0);
      // hasNext/hasPrevious del backend (fila extra del keyset), nunca deducidos del número de página
      this.hayMasPaginas.set(!!resp.hasNext);
      this.hayPaginasAnteriores.set(!!resp.hasPrevious);
      // Solo tras la respuesta exitosa (§17.7): si falla, la página visible no cambia
      this.currentPage.set(
        typeof pagina === 'function' ? pagina(resp) : pagina
      );
    } finally {
      this.loading.set(false);
    }
  }
}
